import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Eye, EyeOff, Lock, Mail } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { Button, Input } from '../components/ui';

const Login = () => {
    const [email, setEmail] = useState('');
    const [password, setPassword] = useState('');
    const [showPassword, setShowPassword] = useState(false);
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState('');
    const { login } = useAuth();
    const navigate = useNavigate();

    const handleSubmit = async (event: React.FormEvent) => {
        event.preventDefault();
        setIsLoading(true);
        setError('');

        try {
            await login(email, password);
            navigate('/attendance');
        } catch {
            setError('Đăng nhập thất bại. Vui lòng kiểm tra lại email và mật khẩu.');
        } finally {
            setIsLoading(false);
        }
    };

    return (
        <main className="relative min-h-screen overflow-hidden bg-[#f5f8ff]">
            {/* Soft brand wash behind both panels, like the original login. */}
            <div
                aria-hidden="true"
                className="pointer-events-none absolute inset-0"
                style={{
                    background:
                        'radial-gradient(circle at 0% 0%, rgba(191, 219, 254, 0.9), transparent 34rem), radial-gradient(circle at 100% 100%, rgba(204, 251, 241, 0.7), transparent 30rem), linear-gradient(135deg, #eef4ff 0%, #f8fafc 50%, #f0fdfa 100%)',
                }}
            />

            <div className="relative mx-auto grid min-h-screen max-w-6xl items-center gap-8 px-4 py-8 sm:px-6 lg:grid-cols-[minmax(0,1fr)_420px] lg:gap-16 lg:px-10">
                <section className="hidden lg:block">
                    <div className="flex items-center gap-3">
                        <img src="/athena-logo.jpg" alt="" className="h-11 w-11 rounded-lg object-cover shadow-sm" />
                        <p className="text-base font-semibold text-ink-900">Athena Education Center</p>
                    </div>

                    <h1 className="mt-16 text-[2.5rem] font-semibold leading-tight tracking-tight text-academy-900">
                        Hệ thống quản lý vận hành
                    </h1>
                    <p className="mt-5 max-w-md text-base leading-7 text-ink-600">
                        Giao diện nội bộ dành cho đội ngũ quản lý, giáo viên và trợ giảng Athena.
                    </p>

                    <ul className="mt-10 grid max-w-md gap-3 text-sm text-ink-700">
                        <li className="flex items-center gap-3"><span className="h-1.5 w-1.5 rounded-full bg-academy-600" />Điểm danh học sinh và nhân sự theo từng buổi</li>
                        <li className="flex items-center gap-3"><span className="h-1.5 w-1.5 rounded-full bg-academy-600" />Lịch học, lớp và cơ sở ở cùng một chỗ</li>
                        <li className="flex items-center gap-3"><span className="h-1.5 w-1.5 rounded-full bg-academy-600" />Bảng công tự tính từ ca đã đăng ký</li>
                    </ul>
                </section>

                <section className="w-full">
                    <div className="mb-6 flex items-center justify-center gap-2.5 lg:hidden">
                        <img src="/athena-logo.jpg" alt="" className="h-10 w-10 rounded-lg object-cover shadow-sm" />
                        <span className="text-lg font-semibold text-ink-900">Athena</span>
                    </div>

                    <div className="rounded-xl border border-white/70 bg-white/90 p-6 shadow-[0_20px_60px_-30px_rgba(11,37,69,0.35)] ring-1 ring-ink-200/60 backdrop-blur sm:p-8">
                        <h2 className="text-2xl font-semibold tracking-tight text-ink-900">Đăng nhập</h2>
                        <p className="mt-1.5 text-sm text-ink-500">Dùng tài khoản được trung tâm cấp để tiếp tục.</p>

                        {error && (
                            <div role="alert" className="mt-5 rounded-lg border border-danger-100 bg-danger-50 px-3 py-2.5 text-sm text-danger-700">
                                {error}
                            </div>
                        )}

                        <form onSubmit={handleSubmit} className="mt-6 space-y-4">
                            <Input
                                id="email"
                                type="email"
                                label="Email"
                                value={email}
                                onChange={(event) => setEmail(event.target.value)}
                                placeholder="nam@athenateach.com"
                                leftIcon={<Mail className="h-4 w-4" />}
                                autoComplete="email"
                                className="min-h-11"
                                required
                                disabled={isLoading}
                            />
                            <Input
                                id="password"
                                type={showPassword ? 'text' : 'password'}
                                label="Mật khẩu"
                                value={password}
                                onChange={(event) => setPassword(event.target.value)}
                                placeholder="Nhập mật khẩu"
                                leftIcon={<Lock className="h-4 w-4" />}
                                autoComplete="current-password"
                                className="min-h-11"
                                rightElement={
                                    <Button
                                        size="icon"
                                        variant="ghost"
                                        className="h-8 w-8"
                                        onClick={() => setShowPassword((visible) => !visible)}
                                        disabled={isLoading}
                                        aria-label={showPassword ? 'Ẩn mật khẩu' : 'Hiện mật khẩu'}
                                        icon={showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                                    />
                                }
                                required
                                disabled={isLoading}
                            />
                            <Button type="submit" variant="primary" className="mt-2 min-h-11 w-full" loading={isLoading}>
                                Đăng nhập
                            </Button>
                        </form>
                    </div>

                    <p className="mt-6 text-center text-xs text-ink-500">© 2026 Athena Education Center</p>
                </section>
            </div>
        </main>
    );
};

export default Login;
